import { DateInput } from "@msuite/ui/date-input";
import { useTranslation } from "@msuite/ui/localization";
import type { CrewDefinitionManagerSlotFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { ManagerPicker } from "@/components/pickers/manager-picker";

/** Props Interface */
interface CrewDefinitionManagerSlotFormProps {
	crewStartDate: string;
	crewEndDate: string | null;
}

/**
 * One manager's window over a standing crew. The span opens on the crew's own dates and cannot be
 * widened past them, because a slot is access *within* a crew's life — a day the crew does not
 * exist on is a day nobody can be given. An open-ended crew bounds nothing at its end, so the
 * window under one may run on as well.
 *
 * **Each end also bounds the other**, so the two pickers cannot offer a window that ends before it
 * begins. The schema refuses that pair anyway, but only once the form is submitted — a day the
 * calendar never offers is the cheaper way to say it.
 */
export function CrewDefinitionManagerSlotForm({
	crewStartDate,
	crewEndDate,
}: CrewDefinitionManagerSlotFormProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<CrewDefinitionManagerSlotFormSchema>();

	/** Computed */
	// The crew's own span is the outer limit; whichever end has been chosen narrows the other
	// inside it. An end the crew does not bound stays open, and so does the field under it.
	const watchedStartDate = methods.watch("startDate");
	const watchedEndDate = methods.watch("endDate");
	const latestStartDate = watchedEndDate || crewEndDate || undefined;
	const earliestEndDate = watchedStartDate || crewStartDate;

	/** Render */
	return (
		<React.Fragment>
			<ManagerPicker
				control={methods.control}
				name="userId"
				label={t("planning.crews.manager_slot_manager")}
				isRequired
			/>
			<div className="gap-4 grid grid-cols-2">
				<DateInput
					control={methods.control}
					name="startDate"
					label={t("planning.crews.manager_slot_start_date")}
					isRequired
					minDate={crewStartDate}
					maxDate={latestStartDate}
				/>
				<DateInput
					control={methods.control}
					name="endDate"
					label={t("planning.crews.manager_slot_end_date")}
					isRequired={false}
					minDate={earliestEndDate}
					maxDate={crewEndDate ?? undefined}
				/>
			</div>
		</React.Fragment>
	);
}
